import React from "react";
import { useState } from "react";
import "src/pages/sendout/Generic/OrderSuccessComponent.css";
import { useTranslation } from "react-i18next";
import { SENDOUT_NAMESPACE } from "src/i18n";

export interface DynamicFormData {
  hash: string;
  name: string;
  description: string;
  address: string;
  city: string;
  zipCode: string;
  region: string;
  date: string;
  participants: {
    id: string;
    firstName: string;
    lastName: string;
    companyName: string;
    role: string;
    email: string;
    phone: string;
  }[];
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function OrderSuccessComponent({
  dynamicFormData
}: { dynamicFormData: DynamicFormData }) {
  const [expandedParticipant, setExpandedParticipant] = useState<string | null>(null);
  const [protocolLink, setProtocolLink] = useState<string>(dynamicFormData.hash ? `/dynamic/${dynamicFormData.hash}` : "#");
  
  const { i18n, t } = useTranslation(SENDOUT_NAMESPACE);

  return (
    <div className="order-success-page">
      <div className="success-wrapper">

        <div className="success-header">
          <div className="checkmark-ring">
            <svg className="checkmark-svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path
                d="M6 14l5.5 5.5L22 8"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="header-eyebrow">{dynamicFormData.name}</p>
          <h1 className="header-title">{t("orderConfirmed")}</h1>
          <p className="header-sub">
            {t("orderSuccessful")}
          </p>
        </div>
        <div className="body-card">

          <div className="section">
            <p className="section-label">{t("handoverAddress")}</p>
            <div className="address-block">
              <div className="address-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.375 4.5 8.5 4.5 8.5s4.5-5.125 4.5-8.5c0-2.485-2.015-4.5-4.5-4.5zm0 6.125A1.625 1.625 0 1 1 8 4.25a1.625 1.625 0 0 1 0 3.375z"
                    fill="#4a4740"
                  />
                </svg>
              </div>
              <div>
                <div className="address-main">{dynamicFormData.address}</div>
                <div className="address-secondary">
                  {dynamicFormData.zipCode} {dynamicFormData.city}, {dynamicFormData.region}
                </div>
                <div className="date-pill">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <rect x="1" y="2" width="11" height="10" rx="1.5" stroke="#7a776f" strokeWidth="1.2" />
                    <path d="M4 1v2M9 1v2M1 5h11" stroke="#7a776f" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                  {formatDate(dynamicFormData.date)}
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <p className="section-label">
              {t("formType", { count: dynamicFormData.participants.length })}
            </p>
            <div className="form-chips">
                <div className="form-chip">
                  <div>{dynamicFormData.name}</div>
                  {dynamicFormData.description && (
                    <div className="form-chip-desc">{dynamicFormData.description}</div>
                  )}
                </div>
            </div>
          </div>

          <div className="section">
            <p className="section-label">{t("protocolDocument")}</p>
            <a
              href={protocolLink}
              target="_blank"
              rel="noopener noreferrer"
              className="protocol-link"
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M3 7.5h9M8.5 4l3.5 3.5L8.5 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("viewProtocol")}
            </a>
          </div>

          <div className="section">
            <p className="section-label">
              {t("protocolParticipants")}
            </p>
            <div className="participants-grid">
              {dynamicFormData.participants.map((p) => {
                const initials = `${p.firstName[0]}${p.lastName[0]}`.toUpperCase();
                const isExpanded = expandedParticipant === p.id;

                return (
                  <div
                    key={p.id}
                    className={`participant-row${isExpanded ? " expanded" : ""}`}
                    onClick={() =>
                      setExpandedParticipant(isExpanded ? null : p.id)
                    }
                  >
                    <div className="participant-summary">
                      <div className="participant-avatar">{initials}</div>
                      <div>
                        <div className="participant-name">
                          {p.firstName} {p.lastName}
                        </div>
                        {p.companyName && (
                          <div className="participant-company">
                            {p.companyName}
                          </div>
                        )}
                      </div>

                      <svg
                        className={`expand-icon${isExpanded ? " open" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M3 5l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    {isExpanded && (
                      <div className="participant-details">
                        <div className="detail-item">
                          <div className="detail-key">{t("email")}</div>
                          <div className="detail-val">{p.email}</div>
                        </div>
                        <div className="detail-item">
                          <div className="detail-key">{t("phone")}</div>
                          <div className="detail-val">{p.phone}</div>
                        </div>
                        {p.companyName && (
                          <div className="detail-item detail-item--full">
                            <div className="detail-key">{t("company")}</div>
                            <div className="detail-val">{p.companyName}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
